import React, { useState, useEffect } from 'react';

interface SavedSession {
  id: number;
  filename: string;
  date: string;
  wordCount: number;
  charCount: number;
  sessionEndedEarly?: boolean;
  sessionCompleted?: boolean;
  numTimesDeleto?: number;
}

interface SavedSessionsListProps {
  onLoad: (sessionId: number) => void;
  onDelete: (sessionId: number) => void;
}

const SavedSessionsList: React.FC<SavedSessionsListProps> = ({
  onLoad,
  onDelete
}) => {
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Load saved sessions from localStorage
  useEffect(() => {
    const loadSessions = () => {
      setIsLoading(true);
      
      try {
        // Get saved sessions from localStorage
        const sessionsData = localStorage.getItem('deletoSessions');
        const sessions: SavedSession[] = sessionsData ? JSON.parse(sessionsData) : [];
        
        setSavedSessions(sessions);
      } catch (err) {
        console.error('Error loading saved sessions:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSessions();
  }, []);
  
  const handleDelete = (sessionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this saved session?')) {
      onDelete(sessionId);
      setSavedSessions(prev => prev.filter(session => session.id !== sessionId));
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading saved sessions...</p>
      </div>
    );
  }
  
  if (savedSessions.length === 0) {
    return (
      <div className="p-4 text-center">
        <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="mt-2 text-gray-600 dark:text-gray-300">No session statistics found</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">Complete writing sessions to see your statistics here. (Text content is never saved)</p>
      </div>
    );
  }
  
  return (
    <div className="overflow-y-auto max-h-80">
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {savedSessions.map(session => (
          <li 
            key={session.id}
            className="p-3 flex justify-between items-center border-b border-gray-100 dark:border-gray-700 last:border-b-0"
          >
            <div>
              <p className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-xs">
                {session.filename || 'Untitled'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {session.charCount} characters • {session.wordCount} words
                {(session.numTimesDeleto && session.numTimesDeleto > 0) && (
                  <span className="text-red-500"> • Got deleto'd {session.numTimesDeleto} time{session.numTimesDeleto === 1 ? '' : 's'}</span>
                )}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(session.date).toLocaleString()}
              </p>
              {(session.sessionEndedEarly || session.sessionCompleted) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {session.sessionEndedEarly ? '⚠️ Ended early' : 
                   session.sessionCompleted ? '✅ Session completed' : ''} • No text stored
                </p>
              )}
            </div>
            <button
              onClick={(e) => handleDelete(session.id, e)}
              className="p-1 text-gray-400 hover:text-danger dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              title="Delete"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SavedSessionsList; 